export default defineNuxtPlugin((nuxtApp) => {
    // figure out the base API url
    const auth = useAuthStore();
    const config = useRuntimeConfig();

    // Track retries per request to prevent infinite loops
    const retryTracker = new WeakMap();

    const api = $fetch.create({
        baseURL: config.public.pb.apiDomain,
        async onRequest({ request, options, error }) {
            // Get a valid token (handles refresh and queueing automatically)
            const token = await auth.getValidTokenForRequest();
            if (token) {
                // note that this relies on ofetch >= 1.4.0 - you may need to refresh your lockfile
                options.headers.set('Authorization', token);
            }
        },
        async onResponseError({ request, response, options }) {
            if (response.status === 401) {
                // Check if we already retried this request (prevent infinite loops)
                const hasRetried = retryTracker.get(options) || false;
                
                if (!hasRetried) {
                    // Mark that we've attempted a retry
                    retryTracker.set(options, true);
                    
                    try {
                        const refreshed = await auth.refreshAccessToken();
                        if (refreshed) {
                            
                            // Get the new token and update headers
                            const newToken = await auth.getValidTokenForRequest();
                            if (newToken) {
                                options.headers.set('Authorization', newToken);
                            }
                            
                            // Create a new $fetch call with the updated options
                            return $fetch(request.toString(), {
                                method: options.method as any,
                                headers: options.headers,
                                body: options.body,
                                baseURL: options.baseURL,
                                query: options.query,
                                params: options.params,
                            });
                        }
                    } catch (error) {
                        console.warn('Token refresh failed in API plugin:', error);
                    }
                }
                
                // If we get here, either refresh failed or we already retried
                console.warn('401 error after token refresh attempt in API plugin');
            }
        },
    });

    return {
        provide: {
            api: {
                call: api,
                routes: {
                    me: {
                        getMe: () => `/me`,
                        updateMe: () => `/me`,
                        akas: {
                            createAka: () => `/me/akas`,
                            deleteAka: (akaId: number) => `/me/akas/${akaId}`,
                        },
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                            createEmail: () => `/me/emails`,
                            deleteEmail: (emailId: number) => `/me/emails/${emailId}`,
                            updateEmail: (emailId: number) => `/me/emails/${emailId}`,
                        },
                        address: {
                            createAddress: () => `/me/addresses`,
                            deleteAddress: (addressId: number) => `/me/addresses/${addressId}`,
                            updateAddress: (addressId: number) => `/me/addresses/${addressId}`,
                        },
                        phones: {
                            /**
                             * POST v3/me/phones/
                             */
                            createPhone: () => `/me/phones`,
                            /**
                             * DELETE v3/me/phones/{phoneId}
                             */
                            deletePhone: (phoneId: number) => `/me/phones/${phoneId}`,
                            /**
                             * POST v3/me/phones/verify
                             */
                            sendVerification: () => `/me/phones/verify`,
                            updatePhone: (phoneId: number) => `/me/phones/${phoneId}`,
                        },

                        poa: {
                            sign: () => `/me/lpoa`,
                            retract: () => `/me/lpoa`,
                        },

                        mfa: {
                            get: () => `/me/mfa`,
                            enable: () => `/me/mfa`,
                            disable: () => `/me/mfa`,
                        },

                        avatar: {
                            upload: () => `/me/avatar`,
                            delete: () => `/me/avatar`,
                        },

                        options: {
                            get: () => `/me/options`,
                            update: () => `/me/options`,
                        },

                        exposures: {
                            suppressions: {
                                get_suppressions: () => `/me/exposures/suppressions`,
                                /**
                                 * PUT v3/me/exposures/suppressions
                                 */
                                update_suppressions: () => `/me/exposures/suppressions`,
                                remove_suppression: (suppressionId: number) => `/me/exposures/suppressions/${suppressionId}`,
                            },
                        },
                        financial: {
                            /**
                             * POST v3/me/financial/subscribe
                             */
                            subscribe: () => `/me/subscribe`,
                            gift_cards: (person_id: number | string, code: string) =>
                                `/people/${person_id}/financial/gift_cards/${code}`,
                            cards: {
                                remove: () => `/me/financial/card`,
                                update: () => `/me/financial/card`,
                            },
                            invoices: {
                                get: (page: number = 1, perPage: number = 10) =>
                                    `/me/financial/invoices?page=${page}&per_page=${perPage}`,
                                download: (invoiceId: string) => `/me/financial/invoices/${invoiceId}`,
                            },
                            creditBalance: {
                                get: () => `/me/financial/credit-balance`,
                            },
                            giftCertificates: {
                                purchase: () => `/me/financial/gift-certificates`,
                                apply: () => `/me/financial/gift-certificates/apply`,
                                email: (code: string) => `/me/financial/gift-certificates/${code}/email`,
                            },
                            confirmPayment: () => `/me/financial/confirm-payment`,
                        },
                    },
                    companies: {
                        deletion_guide: (company_id: number): string => `public/companies/${company_id}/guide`,
                        faqs: (company_id: number): string => `public/companies/${company_id}/faqs`,
                        risk_factors: (company_id: number): string => `public/companies/${company_id}/risk-factors`,
                        advisories: (company_id: number): string => `public/companies/${company_id}/advisories`,
                    },
                    people: {
                        person: (person_id: number) => `/people/${person_id}`,
                        exposures: {
                            exposure: (person_id: number, exposure_id: string) => `/people/${person_id}/exposures/${exposure_id}`,
                            exposure_history: (person_id: number, exposure_id: string) => `/people/${person_id}/exposures/${exposure_id}/history`,
                            activity: (person_id: number) => `/people/${person_id}/exposures/activity`,
                            pipeline: (person_id: number) => `/people/${person_id}/exposures/statistics/pipeline`,
                            statistics: (person_id: number) => `/people/${person_id}/exposures/statistics`,
                            data_exposed: (person_id: number) => `/people/${person_id}/exposures/data-exposed`,
                            detail: (person_id: number) => `/people/${person_id}/exposures/detail`,
                            list: (person_id: number, limit: number = 50) => `/people/${person_id}/exposures?limit=${limit}`,
                            history_export: (person_id: number) => `/people/${person_id}/exposures/history/export`,
                            radar: (person_id: number) => `/people/${person_id}/exposures/radar`,
                            rapid_response: {
                                index: (person_id: number) => `/people/${person_id}/exposures/rapid-response`,
                                store: (person_id: number) => `/people/${person_id}/exposures/rapid-response`,
                                update: (person_id: number, exposure_id: string) => `/people/${person_id}/exposures/rapid-response/${exposure_id}`,
                            },
                        },
                        companies: {
                            get_companies: (person_id: number) => `/people/${person_id}/companies`,
                            get_company: (company_id: number) => `/companies/${company_id}`,
                            search: (query: string, per_page: number, page: number, type: string) =>
                                `/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}`,
                            get_company_relationship: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}`,
                            trust_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/trust`,
                            untrust_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/untrust`,
                            scan_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/scan`,
                            conversation: (person_id: number, company_id: number) => `/people/${person_id}/companies/${company_id}/conversation`,
                            channels: (person_id: number, company_id: number) => `/people/${person_id}/companies/${company_id}/channels`,
                            updateChannel: (person_id: number, company_id: number, channel_id: number) => `/people/${person_id}/companies/${company_id}/channels/${channel_id}`,
                            exposures: (person_id: number, company_id: number) => `/people/${person_id}/companies/${company_id}/exposures`,
                        },



                        contacts: {
                            get_contacts: (person_id: number) => `/people/${person_id}/contacts?per_page=10000&blocked=false`,
                            get_trust_requests: (person_id: number) => `/people/${person_id}/contacts/requests`,
                            create_contact: (person_id: number) => `/people/${person_id}/contacts`,
                            update_contact: (person_id: number, contact_id: number) => `/people/${person_id}/contacts/${contact_id}`,
                            delete: (person_id: number, contact_id: number) => `/people/${person_id}/contacts/${contact_id}`,
                        },

                        //pb university
                        university: {
                            get_statistics: (person_id: number) => `/people/${person_id}/university/statistics`,
                            get_module_info: (person_id: number, module: string) =>
                                `/people/${person_id}/university/modules/${module}`,
                            get_lesson_info: (person_id: number, module: string, lesson_id: number) =>
                                `/people/${person_id}/university/modules/${module}/lesson/${lesson_id}`,
                            update_lesson_progress: (person_id: number, module: string, lesson_id: number) =>
                                `/people/${person_id}/university/modules/${module}/lesson/${lesson_id}`,
                        },

                        licenses: {
                            license: (person_id: number, license_id: number) => `/people/${person_id}/licenses/${license_id}`,
                            purchase_licenses: (person_id: number) => `/people/${person_id}/licenses`,
                            update_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}`,
                            change_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}/change`,
                            refund: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}`,
                        },
                        akas: {
                            create: (person: number) => `/people/${person}/akas`,
                            delete: (person: number, akaId: number) => `/people/${person}/akas/${akaId}`,
                        },
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                            create: (person: number) => `/people/${person}/emails`,
                            delete: (person: number, email: number) => `/people/${person}/emails/${email}`,
                            update: (person: number, email: number) => `/people/${person}/emails/${email}`,
                        },
                        address: {
                            create: (person: number) => `/people/${person}/addresses`,
                            delete: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                            update: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                        },
                        phones: {
                            create: (person: number) => `/people/${person}/phones`,
                            delete: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                            sendVerification: (person: number) => `/people/${person}/phones/verify`,
                            update: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                        },
                        options: {
                            get: (person: number) => `/people/${person}/options`,
                            update: (person: number) => `/people/${person}/options`,
                        },
                    },
                    apps: {
                        private_search: {
                            serach: (query: string, type: string, page: number) =>
                                `/apps/search?type=${type}&q=${query}&page=${page}`,
                            suggest: (query: string) => `/apps/search/suggest?q=${query}`,
                            image_proxy: (image_link: string) => `/apps/search/image?image=${image_link}`,
                        },

                        buzz: {
                            get_activity: (per_page: number) =>
                                `/apps/buzz/activity?start=1&per_page=${per_page ? per_page : 10000}`,
                            log_activity: () => `/apps/buzz/activity`,
                        },
                    },
                    public: {
                        companies: {
                            search: (query: string, per_page: number, page: number, type: string, trust: boolean) =>
                                `/public/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}&trust_page=${trust}`,
                            data_broker: (query: string, per_page: number, page: number, trust: boolean) =>
                                `/public/companies/data_broker?per_page=${per_page}&search=${query}&page=${page}&trust_page=${trust}`,
                            stats: () => `/public/companies/stats`,
                            company: (id: Number, trust: boolean = false) => `/public/companies/${id}?guide=${trust}`,
                            lookup: (slug: string) => `/public/companies/lookup/${slug}?guide=true`,
                            advisories: (company_id: number) => `/public/companies/${company_id}/advisories`,
                            faqs: (company_id: number) => `/public/companies/${company_id}/faqs`,
                            risk_factors: (company_id: number) => `/public/companies/${company_id}/risk-factors`,
                            deletion_guide: (company_id: number) => `/public/companies/${company_id}/guide`,
                        },
                    },
                    countries: {
                        get: () => `/countries`,
                        lookup: (id: number) => `/countries/${id}`,
                    }
                },
            },
        },
    };
});
